import { reportClientErrorOnce } from '@/lib/clientErrorLog';

/**
 * Sorveglianza del microfono locale durante una chiamata.
 *
 * Su Android (in particolare Huawei/EMUI) il sistema può sospendere la
 * cattura del microfono di una tab web quando lo schermo si spegne o il
 * battery saver congela il browser: l'altro lato smette di sentire senza
 * alcun segnale visibile (caso "la consulente non mi sente"). Il browser
 * lo espone come evento `mute`/`unmute` sul MediaStreamTrack — qui li
 * ascoltiamo per avvisare la UI, notificare il peer e lasciare traccia
 * nel Log Errori admin.
 */

export type LocalMicStatus = 'ok' | 'suspended';

export interface MicWatchdog {
  stop(): void;
}

export function watchLocalMic(
  stream: MediaStream,
  callSessionId: string,
  onStatus: (status: LocalMicStatus) => void,
): MicWatchdog {
  const cleanups: Array<() => void> = [];

  const reportSuspended = () => {
    reportClientErrorOnce(`call-mic-suspended-${callSessionId}`, {
      code: 'CALL_MIC_SUSPENDED',
      message:
        'Microfono sospeso dal sistema durante la chiamata: il peer non sente',
      context: { callSessionId, visibility: document.visibilityState },
    });
  };

  for (const track of stream.getAudioTracks()) {
    const onMute = () => {
      reportSuspended();
      onStatus('suspended');
    };
    const onUnmute = () => onStatus('ok');
    const onEnded = () => {
      reportClientErrorOnce(`call-mic-ended-${callSessionId}`, {
        code: 'CALL_MIC_TRACK_ENDED',
        message:
          'Track del microfono terminato dal sistema durante la chiamata',
        context: { callSessionId, visibility: document.visibilityState },
      });
      onStatus('suspended');
    };

    track.addEventListener('mute', onMute);
    track.addEventListener('unmute', onUnmute);
    track.addEventListener('ended', onEnded);
    cleanups.push(() => {
      track.removeEventListener('mute', onMute);
      track.removeEventListener('unmute', onUnmute);
      track.removeEventListener('ended', onEnded);
    });

    // Il track può nascere già muted (permesso dato ma capture non partita).
    if (track.muted) onMute();
  }

  return {
    stop() {
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
    },
  };
}
